// resources/js/Components/heroVideo.ts
// Pause/play toggle for the hero block's autoplaying background video.
//
// WCAG 2.2.2 (Pause, Stop, Hide) requires any auto-starting motion that runs
// longer than 5 seconds to be pausable. The video itself is decorative
// (aria-hidden), so the button's accessible name carries the state: it
// always describes the action a press will perform ("Pause video" /
// "Play video") rather than combining a static label with aria-pressed.
//
// Visitors with prefers-reduced-motion get the video paused from the start,
// but can still opt in with the same button.

export default function initHeroVideo() {
  const wrappers = Array.from(document.querySelectorAll<HTMLElement>('.js-hero-video'))
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

  wrappers.forEach((wrapper) => {
    const video = wrapper.querySelector('video')
    const button = wrapper.querySelector<HTMLButtonElement>('.js-hero-video-toggle')
    const pauseIcon = wrapper.querySelector<SVGElement>('.js-hero-video-icon-pause')
    const playIcon = wrapper.querySelector<SVGElement>('.js-hero-video-icon-play')

    if (!video || !button || !pauseIcon || !playIcon) {
      return
    }

    // Synced from the video's own play/pause events rather than set in the
    // click handler, so the button also stays correct if the browser blocks
    // autoplay (e.g. iOS Low Power Mode) and the video never starts.
    const syncButton = () => {
      const isPlaying = !video.paused
      button.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video')
      pauseIcon.toggleAttribute('hidden', !isPlaying)
      playIcon.toggleAttribute('hidden', isPlaying)
    }

    video.addEventListener('play', syncButton)
    video.addEventListener('pause', syncButton)

    button.addEventListener('click', () => {
      if (video.paused) {
        void video.play()
      } else {
        video.pause()
      }
    })

    if (reducedMotion.matches) {
      video.pause()
    }

    syncButton()
  })
}
